import { createSlice } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
import { SUBSCRIPTION_INVOICE_API } from "../../config-global";

const DEFAULT_LIMIT = 999999999;

const initialState = {
  isLoading: false,
  isPaymentsLoading: false,
  isWarningLoading: false,
  error: null,
  warningError: null,
  invoices: [],
  payments: [],
  paymentsTotalResults: 0,
  warningInvoice: null,
  warningTotalResults: 0,
  invoice: null,
  page: 1,
  limit: DEFAULT_LIMIT,
  totalPages: 0,
  totalResults: 0,
};

function getErrorMessage(error, fallback) {
  if (typeof error === "string") {
    return error;
  }

  return error?.message || error?.error || fallback;
}

function normalizeInvoice(invoice) {
  if (!invoice || typeof invoice !== "object") {
    return invoice;
  }

  return {
    ...invoice,
    id: invoice.id || invoice._id,
  };
}

function normalizeListResponse(data, fallbackPage, fallbackLimit) {
  const payload = data?.data || data;
  const resultPayload = payload?.result;
  const results =
    payload?.results ||
    payload?.invoices ||
    resultPayload?.results ||
    resultPayload?.invoices ||
    resultPayload ||
    [];
  const invoices = Array.isArray(results) ? results.map(normalizeInvoice) : [];
  const pagination =
    resultPayload && !Array.isArray(resultPayload) ? resultPayload : payload;

  return {
    invoices,
    page: pagination?.page || fallbackPage,
    limit: pagination?.limit || fallbackLimit,
    totalPages:
      pagination?.totalPages ||
      Math.ceil(
        (pagination?.totalResults || invoices.length) / fallbackLimit
      ),
    totalResults:
      pagination?.totalResults ?? pagination?.total ?? invoices.length,
  };
}

function normalizeDetailResponse(data) {
  const invoice =
    data?.data?.result ||
    data?.data?.invoice ||
    data?.data ||
    data?.result ||
    data?.invoice ||
    data;

  return normalizeInvoice(invoice);
}

const slice = createSlice({
  name: "subscriptionInvoice",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    getInvoicesSuccess(state, action) {
      state.isLoading = false;
      state.invoices = action.payload.invoices;
      state.page = action.payload.page;
      state.limit = action.payload.limit;
      state.totalPages = action.payload.totalPages;
      state.totalResults = action.payload.totalResults;
    },
    getInvoiceSuccess(state, action) {
      state.isLoading = false;
      state.invoice = action.payload;
    },
    getPaymentsSuccess(state, action) {
      state.isPaymentsLoading = false;
      state.payments = action.payload.payments;
      state.paymentsTotalResults = action.payload.totalResults;
    },
    startPaymentsLoading(state) {
      state.isPaymentsLoading = true;
      state.error = null;
    },
    hasPaymentsError(state, action) {
      state.isPaymentsLoading = false;
      state.error = action.payload;
    },
    startWarningLoading(state) {
      state.isWarningLoading = true;
      state.warningError = null;
    },
    getWarningInvoiceSuccess(state, action) {
      state.isWarningLoading = false;
      state.warningInvoice = action.payload.invoice;
      state.warningTotalResults = action.payload.totalResults;
    },
    hasWarningError(state, action) {
      state.isWarningLoading = false;
      state.warningError = action.payload;
      state.warningInvoice = null;
      state.warningTotalResults = 0;
    },
    clearInvoice(state) {
      state.invoice = null;
      state.error = null;
    },
  },
});

export const { clearInvoice } = slice.actions;

export default slice.reducer;

export function getSubscriptionInvoices({
  franchiseId,
  status,
  paymentStatus,
  page = 1,
  limit = DEFAULT_LIMIT,
}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const params = { franchiseId, limit };

      if (status) {
        params.status = status;
      }

      if (paymentStatus) {
        params.paymentStatus = paymentStatus;
      }

      const response = await axios.get(`${SUBSCRIPTION_INVOICE_API}`, {
        params,
      });

      dispatch(
        slice.actions.getInvoicesSuccess(
          normalizeListResponse(response.data, page, limit)
        )
      );
    } catch (error) {
      dispatch(
        slice.actions.hasError(
          getErrorMessage(error, "Unable to load subscription invoices.")
        )
      );
    }
  };
}

export function getSubscriptionPayments({ franchiseId, page = 1, limit = DEFAULT_LIMIT }) {
  return async (dispatch) => {
    dispatch(slice.actions.startPaymentsLoading());

    try {
      const response = await axios.get(`${SUBSCRIPTION_INVOICE_API}/payments`, {
        params: { franchiseId, page, limit },
      });
      const normalized = normalizeListResponse(response.data, page, limit);
      dispatch(
        slice.actions.getPaymentsSuccess({
          payments: normalized.invoices,
          totalResults: normalized.totalResults,
        })
      );
    } catch (error) {
      dispatch(
        slice.actions.hasPaymentsError(
          getErrorMessage(error, "Unable to load subscription payments.")
        )
      );
    }
  };
}

export function getSubscriptionWarningInvoice({ franchiseId }) {
  return async (dispatch) => {
    dispatch(slice.actions.startWarningLoading());

    try {
      const response = await axios.get(`${SUBSCRIPTION_INVOICE_API}`, {
        params: {
          franchiseId,
          status: "init",
          limit: 1,
        },
      });
      const payload = normalizeListResponse(response.data, 1, 1);

      dispatch(
        slice.actions.getWarningInvoiceSuccess({
          invoice: payload.invoices[0] || null,
          totalResults: payload.totalResults,
        })
      );
    } catch (error) {
      dispatch(
        slice.actions.hasWarningError(
          getErrorMessage(error, "Unable to load subscription warning.")
        )
      );
    }
  };
}

export function getSubscriptionInvoice(invoiceId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(
        `${SUBSCRIPTION_INVOICE_API}/${invoiceId}`
      );

      dispatch(
        slice.actions.getInvoiceSuccess(normalizeDetailResponse(response.data))
      );
      return normalizeDetailResponse(response.data);
    } catch (error) {
      dispatch(
        slice.actions.hasError(
          getErrorMessage(error, "Unable to load subscription invoice details.")
        )
      );
      throw error;
    }
  };
}

// Stores an invoice detail the backend already returned (e.g. from the gateway
// reconcile endpoint) without issuing another GET for the same invoice.
export function setSubscriptionInvoiceDetail(data) {
  return (dispatch) => {
    const invoice = normalizeDetailResponse(data);
    dispatch(slice.actions.getInvoiceSuccess(invoice));
    return invoice;
  };
}

export function createSubscriptionInvoicePayment(invoiceId, payload, options = {}) {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${SUBSCRIPTION_INVOICE_API}/${invoiceId}/payment`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (options.franchiseId) {
        await dispatch(
          getSubscriptionInvoices({
            franchiseId: options.franchiseId,
            limit: DEFAULT_LIMIT,
          })
        );
        await dispatch(
          getSubscriptionWarningInvoice({ franchiseId: options.franchiseId })
        );
      }

      return response.data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, "Unable to create subscription payment.")
      );
    }
  };
}
